/**
 * Duration formatting primitives shared across the app.
 *
 * All functions take seconds (except formatPlayerTime which also takes
 * seconds) and every h/m/s decomposition goes through splitDuration so the
 * math lives in exactly one place.
 */

export interface DurationParts {
  hours: number;
  minutes: number;
  seconds: number;
}

function pad(value: number, length = 2): string {
  return value.toString().padStart(length, "0");
}

/**
 * Splits a non-negative number of seconds into whole hours/minutes/seconds.
 * Fractional seconds are floored; negative values clamp to zero.
 */
export function splitDuration(totalSeconds: number): DurationParts {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
  return {
    hours: Math.floor(safeSeconds / 3600),
    minutes: Math.floor((safeSeconds % 3600) / 60),
    seconds: safeSeconds % 60,
  };
}

/**
 * Human style: "1h 2m 5s", "2m 5s", "5s".
 */
export function formatDurationHuman(totalSeconds: number): string {
  const { hours, minutes, seconds } = splitDuration(totalSeconds);
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Clock style: "m:ss" under an hour, "h:mm:ss" at or above it.
 * Floors fractional seconds and clamps negatives to zero.
 */
export function formatDurationClock(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds || 0));
  const { hours, minutes, seconds } = splitDuration(safeSeconds);
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${minutes}:${pad(seconds)}`;
}

/**
 * Upload-ETA style: whole seconds under a minute, "m s" beyond it.
 * Minutes never roll into hours ("61m 1s", not "1h 1m 1s").
 * Fractional seconds are rounded.
 */
export function formatUploadDuration(totalSeconds: number): string {
  const total = Math.round(totalSeconds || 0);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}m ${seconds}s`;
}

/**
 * Player transport clock: like formatDurationClock but sign-aware
 * ("-0:42") and rendering "0:00" for non-finite input.
 */
export function formatPlayerTime(time: number): string {
  if (!Number.isFinite(time)) {
    return "0:00";
  }
  const sign = time < 0 ? "-" : "";
  const total = Math.floor(Math.abs(time));
  const { hours, minutes, seconds } = splitDuration(total);
  if (hours > 0) {
    return `${sign}${hours}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${sign}${minutes}:${pad(seconds)}`;
}
